import { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import API, { applyShopTheme } from '@/lib/shopApi';
import { ShoppingCart, ListChecks, Receipt, User, SignOut, Storefront } from '@phosphor-icons/react';

export default function ShopLayout() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    API.get('/shop/me')
      .then((r) => { setMe(r.data); applyShopTheme(r.data?.leverancier?.primary_kleur); })
      .catch(() => navigate('/shop/login'));
    const refresh = () => {
      const c = JSON.parse(localStorage.getItem('shop_cart') || '[]');
      setCartCount(c.reduce((s, x) => s + Number(x.aantal || 0), 0));
    };
    refresh();
    window.addEventListener('shop_cart_change', refresh);
    return () => window.removeEventListener('shop_cart_change', refresh);
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem('shop_token');
    localStorage.removeItem('shop_cart');
    navigate('/shop/login');
  };

  if (!me) return <div className="p-12 text-center text-gray-400">Laden…</div>;

  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const logoUrl = me.leverancier?.logo ? `${apiBase}${me.leverancier.logo}` : null;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white sticky top-0 z-30 shadow-sm" style={{ borderBottom: '2px solid var(--shop-primary)' }}>
        {/* Top bar — leverancier prominent */}
        <div className="text-white" style={{ background: 'linear-gradient(to right, var(--shop-primary), var(--shop-primary-dark))' }}>
          <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              {logoUrl ? (
                <img src={logoUrl} alt={me.leverancier?.naam} className="h-8 w-auto max-w-[120px] bg-white p-1 object-contain" />
              ) : (
                <Storefront size={26} weight="bold" />
              )}
              <div className="min-w-0">
                <div className="text-base sm:text-lg font-black truncate" data-testid="shop-leverancier-naam">
                  {me.leverancier?.naam || 'Online Shop'}
                </div>
                <div className="text-[10px] sm:text-xs opacity-80 truncate">
                  Online bestel-portaal voor zakelijke klanten
                </div>
              </div>
            </div>
            <div className="hidden sm:flex items-center gap-3 text-[11px] opacity-90 shrink-0">
              {me.leverancier?.tel && <span>📞 {me.leverancier.tel}</span>}
              {me.leverancier?.email && <a href={`mailto:${me.leverancier.email}`} className="hover:underline">✉ {me.leverancier.email}</a>}
            </div>
          </div>
        </div>
        {/* Klant + nav */}
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-3">
          <div className="text-xs text-gray-600">
            <span className="text-gray-400">Ingelogd als</span>{' '}
            <span className="font-bold text-gray-800">{me.klant?.naam}</span>
            {me.klant?.kortinggroep && (
              <span className="ml-2 inline-block bg-purple-100 text-purple-700 px-1.5 py-0.5 text-[10px] font-bold uppercase">
                {me.klant.kortinggroep}
              </span>
            )}
          </div>
          <nav className="hidden md:flex items-center gap-1 text-sm">
            <NavLink to="/shop/bestellen" className={({ isActive }) => `px-3 py-2 font-semibold ${isActive ? 'shop-nav-active' : 'text-gray-600 hover:opacity-80'}`} data-testid="shop-nav-bestellen">
              <ShoppingCart size={14} weight="bold" className="inline mr-1" /> Bestellen
            </NavLink>
            <NavLink to="/shop/bestellingen" className={({ isActive }) => `px-3 py-2 font-semibold ${isActive ? 'shop-nav-active' : 'text-gray-600 hover:opacity-80'}`} data-testid="shop-nav-bestellingen">
              <ListChecks size={14} weight="bold" className="inline mr-1" /> Mijn bestellingen
            </NavLink>
            <NavLink to="/shop/facturen" className={({ isActive }) => `px-3 py-2 font-semibold ${isActive ? 'shop-nav-active' : 'text-gray-600 hover:opacity-80'}`} data-testid="shop-nav-facturen">
              <Receipt size={14} weight="bold" className="inline mr-1" /> Facturen
            </NavLink>
          </nav>
          <div className="flex items-center gap-2">
            <NavLink to="/shop/mandje" className="relative inline-flex items-center gap-1 px-3 py-2 text-sm font-bold text-white shop-btn-primary" data-testid="shop-mandje-link">
              <ShoppingCart size={16} weight="bold" />
              <span>Mandje</span>
              {cartCount > 0 && (
                <span className="bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">{cartCount}</span>
              )}
            </NavLink>
            <button onClick={logout} className="text-gray-400 hover:text-red-500 p-2" title="Uitloggen" data-testid="shop-logout">
              <SignOut size={18} weight="bold" />
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto p-4">
        <Outlet context={{ me }} />
      </main>
      {/* Footer met leverancier-info */}
      <footer className="mt-12 py-6 text-center text-[11px] text-gray-400 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4">
          Online shop van <strong className="text-gray-600">{me.leverancier?.naam}</strong>
          {me.leverancier?.email && <> · <a href={`mailto:${me.leverancier.email}`} className="hover:text-[#002FA7]">{me.leverancier.email}</a></>}
        </div>
      </footer>
    </div>
  );
}
